/**
 * Feature Flags Configuration
 *
 * Environment variables should be prefixed with CCP_FEATURE_ and set to 'true' or 'false'
 * Example: CCP_FEATURE_FLEET=true
 */
import { typedEntries } from '@/utils/typed-entries.ts'

export const FEATURES = {
  FLEET: 'FLEET',
  FLEET_TRACKING: 'FLEET_TRACKING',
  FLEET_ALLOCATION: 'FLEET_ALLOCATION',
  CHARGING: 'CHARGING',
  ROUTES: 'ROUTES',
  REPORTS: 'REPORTS',
  USER_MANAGEMENT: 'USER_MANAGEMENT',
  DASHBOARD: 'DASHBOARD',
  ADMINISTRATION: 'ADMINISTRATION',
  MARKETPLACE: 'MARKETPLACE',
  REPORTING: 'REPORTING'
} as const

export type Feature = (typeof FEATURES)[keyof typeof FEATURES]

// Default feature flags (can be overridden by environment variables)
const defaultFeatureFlags: Record<Feature, boolean> = {
  [FEATURES.FLEET]: true,
  [FEATURES.FLEET_TRACKING]: true,
  [FEATURES.FLEET_ALLOCATION]: true,
  [FEATURES.CHARGING]: true,
  [FEATURES.ROUTES]: true,
  [FEATURES.REPORTS]: true,
  [FEATURES.USER_MANAGEMENT]: true,
  [FEATURES.DASHBOARD]: true,
  [FEATURES.ADMINISTRATION]: true,
  [FEATURES.MARKETPLACE]: true,
  [FEATURES.REPORTING]: true
}

/**
 * Get feature flag value from environment or default
 */
function getFeatureFlag(feature: Feature): boolean {
  const envKey = `VITE_FEATURE_${feature}`
  const envValue = import.meta.env[envKey]

  // If environment variable is set, use it (convert string to boolean)
  if (envValue !== undefined) {
    return envValue === 'true' || envValue === true
  }

  // Otherwise use default
  return defaultFeatureFlags[feature]
}

/**
 * Feature flags configuration object
 */
export const featureFlags: Record<Feature, boolean> = {
  [FEATURES.FLEET]: getFeatureFlag(FEATURES.FLEET),
  [FEATURES.FLEET_TRACKING]: getFeatureFlag(FEATURES.FLEET_TRACKING),
  [FEATURES.FLEET_ALLOCATION]: getFeatureFlag(FEATURES.FLEET_ALLOCATION),
  [FEATURES.CHARGING]: getFeatureFlag(FEATURES.CHARGING),
  [FEATURES.ROUTES]: getFeatureFlag(FEATURES.ROUTES),
  [FEATURES.REPORTS]: getFeatureFlag(FEATURES.REPORTS),
  [FEATURES.USER_MANAGEMENT]: getFeatureFlag(FEATURES.USER_MANAGEMENT),
  [FEATURES.DASHBOARD]: getFeatureFlag(FEATURES.DASHBOARD),
  [FEATURES.ADMINISTRATION]: getFeatureFlag(FEATURES.ADMINISTRATION),
  [FEATURES.MARKETPLACE]: getFeatureFlag(FEATURES.MARKETPLACE),
  [FEATURES.REPORTING]: getFeatureFlag(FEATURES.REPORTING)
}

/**
 * Check if a feature is enabled
 */
export function isFeatureEnabled(feature: Feature): boolean {
  return featureFlags[feature] ?? false
}

/**
 * Get all enabled features
 */
export function getEnabledFeatures(): Feature[] {
  return typedEntries(featureFlags)
    .filter(([, enabled]) => enabled)
    .map(([feature]) => feature)
}

/**
 * Get all disabled features
 */
export function getDisabledFeatures(): Feature[] {
  return typedEntries(featureFlags)
    .filter(([, enabled]) => !enabled)
    .map(([feature]) => feature)
}

/**
 * Guard function for route beforeLoad hooks
 * Throws a redirect to home page if feature is disabled
 */
export function requireFeature(feature: Feature) {
  if (!isFeatureEnabled(feature)) {
    throw new Error(`Feature ${feature} is disabled`)
  }
}
